import { type VariantProps, cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

/**
 * 분류 배지 / 상태 태그.
 *
 * D1 §3: 색은 예쁘라고 있는 게 아니라 분류를 위해 있다.
 * 상태는 아래 4패밀리(brand / gold / seal / good)로만 표현한다.
 * 새 색을 만들면 분류 체계가 깨진다.
 *
 * 오렌지 tone은 의도적으로 없다 — 오렌지는 상태가 아니라 흐름을 뜻한다(D1 §3).
 */
export const badgeVariants = cva(
  // [D4-05] font-bold → font-medium. 배지는 이미 색면으로 눈에 띈다.
  // 굵기까지 얹으면 화면의 모든 조각이 동시에 소리치게 된다.
  // w-fit: 세로 flex 부모(PageShell 등) 안에서 줄 폭으로 늘어나지 않는다 — Funding `출시 예정` 배지가 672px로 늘었다.
  'inline-flex w-fit items-center gap-1 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        /** 내 것 · 선택됨 · 논문 · PI 역할 · 내 프로필 출처 */
        brand: 'bg-brand-soft text-brand-600',
        /** 특허 · 지침/규정 출처 · 다가오는 마일스톤 · 주의 */
        gold: 'bg-gold-soft text-gold',
        /** 인증서 · 리뷰 지적 · 긴급 D-day · 미열람 · 검증 필요 */
        seal: 'bg-seal-soft text-seal',
        /** 확정됨 · 완료 · 진행중 과제 · 여유 D-day · 모집중 */
        good: 'bg-good-soft text-good',
        /** 법령·조항 출처 전용 */
        law: 'bg-law-soft text-law',
        /** 분류가 없는 메타 정보 (버전, 개수 등) */
        neutral: 'bg-canvas text-ink-muted border-line border',

        /* --- 시스템 피드백 별칭. 폼 검증·토스트 등에 쓴다. --- */
        success: 'bg-success-soft text-success',
        warning: 'bg-warning-soft text-warning',
        danger: 'bg-danger-soft text-danger',
      },
      shape: {
        /** 분류 태그. D1 §5 — 배지는 4px 각진 모서리 */
        tag: 'px-tight rounded-sm py-0.5',
        /** 필터·선택 칩. 클릭 가능한 것에만 쓴다 */
        pill: 'rounded-pill px-2.5 py-0.5',
      },
    },
    defaultVariants: { tone: 'neutral', shape: 'tag' },
  },
);

export interface BadgeProps extends ComponentProps<'span'>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, shape, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, shape }), className)} {...props} />;
}
